import { ActionBar, Label, Modal } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { type Column, GenericTable } from '../../../../components/GenericTable'
import { trpc } from '../../../../utils/trpc'
import type { User, UserSearchInput } from '../../../users/types'

interface AddReviewersModalWrapperProps {
	isOpen: boolean
	excludedUserExternalIds: () => Set<string>
	onSelect: (users: User[]) => void
	onClose: () => void
	organizationId?: string
}

export const AddReviewersModalWrapper = ({
	isOpen,
	onClose,
	organizationId,
	excludedUserExternalIds,
	onSelect,
}: AddReviewersModalWrapperProps) => {
	// Maintain selection across pages by storing user IDs
	const [allSelectedUsers, setAllSelectedUsers] = useState<User[]>([])

	// Log selection changes
	useEffect(() => {
		console.log('[AddReviewersModalWrapper] Total selection:', allSelectedUsers.length, allSelectedUsers)
	}, [allSelectedUsers])

	// Reset selection when modal closes
	useEffect(() => {
		if (!isOpen) {
			console.log('[AddReviewersModalWrapper] Modal closed, resetting selection')
			setAllSelectedUsers([])
		}
	}, [isOpen])

	const handleSelectionChange = (users: User[]) => {
		console.log('[AddReviewersModalWrapper] Page selection changed:', users.length, users)

		// Get IDs of users on current page
		const currentPageUserIds = new Set(users.map((u) => u.id))

		// Remove any IDs that were deselected on current page
		// Add any IDs that were selected on current page
		const updatedIds = new Set<string>()

		// Keep all previously selected users that aren't on the current page
		allSelectedUsers.forEach((user) => {
			if (!currentPageUserIds.has(user.id)) {
				updatedIds.add(user.id)
			}
		})

		// Add all currently selected users
		users.forEach((user) => {
			updatedIds.add(user.id)
		})

		// Rebuild the full list of selected users
		const updatedUsers = [
			...allSelectedUsers.filter((u) => !currentPageUserIds.has(u.id)), // Keep users from other pages
			...users, // Add users from current page
		]

		setAllSelectedUsers(updatedUsers)
		console.log('[AddReviewersModalWrapper] Updated total selection:', updatedUsers.length)
	}

	const handleAddUsers = (e: React.MouseEvent) => {
		e.preventDefault()
		console.log('[AddReviewersModalWrapper] Adding users:', allSelectedUsers.length, allSelectedUsers)
		onSelect(allSelectedUsers)
		onClose()
	}

	const columns: Column<User & { customFields: Record<string, string> }>[] = [
		{
			field: 'firstName',
			label: 'Name',
			colSpan: 2,
			render: (user: User) => `${user.firstName} ${user.lastName}`,
		},
		{
			field: 'externalId',
			label: 'ID',
			colSpan: 1,
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
		},
		{
			field: 'active',
			label: 'Status',
			render: (user: User) => (
				<div className='flex'>
					<Label className='self-start' color={user.active ? 'green' : 'default'}>
						{user.active ? 'Active' : 'Inactive'}
					</Label>
				</div>
			),
		},
	]

	return (
		<Modal isOpen={isOpen} fullContent onClose={onClose} fullScreen title='Add Users'>
			{allSelectedUsers.length > 0 && (
				<div className='px-6 py-3 bg-blue-50 border-b border-blue-200'>
					<span className='text-blue-700 font-medium'>
						{allSelectedUsers.length} user{allSelectedUsers.length !== 1 ? 's' : ''} selected
					</span>
				</div>
			)}

			<div className='flex flex-col h-[calc(100vh-200px)]'>
				<GenericTable<User & { customFields: Record<string, string> }, UserSearchInput>
					queryOptionsGenerator={(options) => ({
						...trpc.admin.users.search.queryOptions({
							...options,
							organizationId: organizationId as string,
							excludingIds: Array.from(excludedUserExternalIds()),
							excludeCustomFields: true,
						}),
					})}
					isInternal
					selectable
					onSelectionChange={handleSelectionChange}
					columns={columns}
				/>
			</div>

			<ActionBar
				variant='modal'
				className='left-0'
				rightActions={[
					{
						label: 'Cancel',
						variant: 'secondary',
						onClick: (e) => {
							e.preventDefault()
							onClose()
						},
					},
					{
						label:
							allSelectedUsers.length === 0
								? 'Select users to continue'
								: `Add ${allSelectedUsers.length} user${allSelectedUsers.length !== 1 ? 's' : ''}`,
						variant: 'primary',
						onClick: handleAddUsers,
						disabled: allSelectedUsers.length === 0,
					},
				]}
			/>
		</Modal>
	)
}
